import { Component, effect, inject, signal, ChangeDetectionStrategy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { Seo } from '../services/seo';
import { LastUpdate } from '../services/last-update';
import { NavActions } from '../services/nav-actions';
import { navItem, navData } from '../data/navData';
import { form, FormField } from '@angular/forms/signals';
import { SearchPages } from '../classes/searchPages';

interface iSearch { term: string };

@Component({
  selector: 'app-nav',
  imports: [RouterLink, FormField],
  templateUrl: './nav.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './nav.scss',
})
export class Nav {
  private router = inject(Router);
  private seo = inject(Seo);
  private platformId = inject(PLATFORM_ID);

  navServ = inject(NavActions);
  lastUpdate = inject(LastUpdate);
  searchEngine!: SearchPages;
  data: navItem[] = navData;
  menuItems: { [key: string]: navItem[] } = {};
  currentKey = '';
  sortChrono = true;
  currentUrl = '';

  searchModel = signal<iSearch>({
    term: '',
  });

  searchForm = form(this.searchModel);

  submit = () => {
    this.menuItems = {};
    this.sortChrono ? this.sortCentury() : this.sortAlphabetical();
    this.setActiveLink();
    return false;
  }

  toggleSort = () => {
    this.menuItems = {};
    this.sortChrono = !this.sortChrono;
    this.sortChrono ? this.sortCentury() : this.sortAlphabetical();
    this.setActiveLink();
  }

  parseAlphabetically = (item: navItem) => {
    const {show} = item;
    const partial = show.substring(0, 1);
    return `${partial.toLocaleUpperCase()}`;
  }

  parseCentury = (item: navItem) => {
    const {time} = item;
    const partial = time / 100;
    const ce = partial < 0 ? 'BCE' : 'CE';
    const absolute = Math.floor(Math.abs(partial));
    const decmial = partial.toString().split('.')[1];
    const century = decmial ? absolute + 1 : absolute;
    let suffix = 'th';
    if (century > 20 || century < 4) {
      const regEnd = [...century.toString().matchAll(/\d$/gm)].flat()[0];
      // console.log(regEnd);
      switch (parseInt(regEnd)) {
        case 1: suffix = 'st' 
          break;
        case 2: suffix = 'nd'
          break;
        case 3: suffix = 'rd'
          break;
      }
    }
    return  `${decmial ? absolute + 1 : absolute}<sup>${suffix}</sup> <small>${ce}</small>`;
  }

  getNavKeys = () => {
    return Object.keys(this.menuItems);
  }

  getNavItems = (key: string): navItem[] => {
    return this.menuItems[key];
  }

  setKey = (key: string) => {
    this.navServ.expandMenu(key);
  }

  private filterMenu = (f: navItem) => {
      const val = this.searchForm.term().value().toLocaleLowerCase().trim();
      const parts = val.split(/\s/g);
      let doFilter = (val === '' && !f.display) ? (f.display !== false)
        : f.show.toLocaleLowerCase().indexOf(val) !== -1
        || parts.filter((p) => { return this.searchEngine.has(f.path, p.toLocaleUpperCase()); })?.length > 0;

      return doFilter;
  }

  sortCentury = () => {
    const srt = (a: navItem, b: navItem) => {
      return a.time - b.time;
    };

    this.data.sort(srt).filter(this.filterMenu).forEach((m) => {
      const century = this.parseCentury(m);
      if (Object.hasOwn(this.menuItems, century)) {
        this.menuItems[century].push(m);
      } else {
        this.menuItems[century] = [m];
      }
    });
  }

  sortAlphabetical = () => {
    const srt = (a: navItem, b: navItem) => {
      return a.show.localeCompare(b.show);
    };

    this.data.sort(srt).filter(this.filterMenu).forEach((m) => {
      const alpha = this.parseAlphabetically(m);
      if (Object.hasOwn(this.menuItems, alpha)) {
        this.menuItems[alpha].push(m);
      } else {
        this.menuItems[alpha] = [m];
      }
    });
  }

  setActiveLink() {
    // slight delay to highlight link.
    setTimeout(() => {
      if (isPlatformBrowser(this.platformId)) {
        let anchors = document.getElementsByTagName('a');
        [...anchors].forEach(a => a.classList.remove('active'));
        const currentPath = [...anchors].filter(a => { return a.pathname === (this.currentUrl.indexOf('?') !== -1 ? this.currentUrl.substring(0, this.currentUrl.indexOf('?')) : this.currentUrl)});
        if (currentPath.length > 0) {
          currentPath.forEach((c) => {
            c.classList.add('active');
          });
        }
      }
    }, 16);
  }

  constructor() {
    this.searchEngine = new SearchPages();
    this.seo.updateCanonical();
    this.sortCentury();
    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        this.seo.updateCanonical();
        await this.lastUpdate.checkForUpdates();
        this.currentUrl = event.urlAfterRedirects;
        this.setActiveLink();
        if (isPlatformBrowser(this.platformId)) {
          if (!window.location.hash.includes(`:~:text=`)) {
            window.scrollTo(0, 0);
          }
        }
      }
    });

    effect(async () => {
      this.currentKey = this.navServ.expand();
    });
  }

  public keyClick = (key: string) => {
    this.navServ.expandMenu(key);
  }

  public menuclick = () => {
    this.navServ.toggle(!this.navServ.current());
  }
}
